import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  CreditCard,
  Landmark,
  BadgeEuro,
  Coins,
  ShieldCheck,
} from "lucide-react";
import Button from "./Button";
import Alert from "./Alert"; // <--- Ścieżka do Twojego Alert.jsx
import Loader from "./Loader"; // <--- Ścieżka do Twojego Loader.jsx
import api from "../../utils/api"; // <--- Ścieżka do Twojego api.js
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const PRESETS = [10, 20, 50, 100];

function parseAmountToNumber(amountStr) {
  if (!amountStr) return 0;
  const normalized = amountStr.replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function DonateModal({
  open,
  onClose,
  projectId,
  projectSlug,
  projectTitle,
  defaultCurrency = "PLN",
  bankAccounts,
}) {
  const { t } = useTranslation("payment");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [method, setMethod] = useState("transfer");
  const [amountStr, setAmountStr] = useState("50");

  // NOWE STANY DLA INTEGRACJI STRIPE:
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCurrency(defaultCurrency);
    setMethod("transfer");
    setAmountStr("50");
    setErrorMsg(null); // Resetujemy błąd po otwarciu
    setIsLoading(false);
  }, [open, defaultCurrency]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const account = useMemo(() => {
    return bankAccounts?.[currency] || null;
  }, [bankAccounts, currency]);

  const amountNumber = useMemo(
    () => parseAmountToNumber(amountStr),
    [amountStr],
  );

  const transferTitle = useMemo(() => {
    if (projectTitle) {
      return t("donate.transfer.titleWithProject", { title: projectTitle });
    }
    return t("donate.transfer.defaultTitle");
  }, [projectTitle, t]);

  const handlePreset = (v) => {
    setAmountStr(String(v));
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value;

    // pozwól na czyszczenie
    if (raw === "") {
      setAmountStr("");
      return;
    }

    // zostaw tylko cyfry + , .
    const cleaned = raw.replace(/[^\d,.\s]/g, "").replace(/\s+/g, "");

    // weź pierwszy separator, resztę usuń (żeby nie było 12,3,4)
    const firstSepIndex = cleaned.search(/[,.]/);
    let normalized = cleaned;
    if (firstSepIndex !== -1) {
      const before = cleaned.slice(0, firstSepIndex);
      const after = cleaned.slice(firstSepIndex + 1).replace(/[,.]/g, "");
      const sep = cleaned[firstSepIndex]; // zostawia , lub .
      normalized = `${before}${sep}${after}`;
    }

    // ✅ BLOKADA: max 2 cyfry po separatorze
    // pozwalamy: "12", "12,", "12,3", "12,34" oraz analogicznie z kropką
    const ok = /^(\d+)([,.]\d{0,2})?$/.test(normalized);

    if (!ok) {
      // nie aktualizuj stanu -> użytkownik nie "dopcha" 3 cyfry po separatorze
      return;
    }

    // limit długości (opcjonalnie)
    setAmountStr(normalized.slice(0, 10));
  };

  const handleAmountBlur = () => {
    if (amountStr === "") return;
    const n = parseAmountToNumber(amountStr);
    if (!n) {
      setAmountStr("");
      return;
    }
    const fixed = n
      .toFixed(2)
      .replace(/\.00$/, "")
      .replace(/(\.\d)0$/, "$1");
    const wantsComma = amountStr.includes(",");
    setAmountStr(wantsComma ? fixed.replace(".", ",") : fixed);
  };

  const handleContinue = async () => {
    const n = parseAmountToNumber(amountStr);

    if (!n || n <= 0) {
      setErrorMsg(t("donate.errors.invalidAmount"));
      return;
    }

    if (method === "stripe") {
      setIsLoading(true);
      setErrorMsg(null);

      try {
        // Stripe przyjmuje kwoty w najmniejszej jednostce (np. grosze, centy)
        const amountInSmallestUnit = Math.round(n * 100);

        // Wysyłamy zapytanie do nowej ścieżki za pomocą Twojego instancji api
        const response = await api.post("/payments/create-checkout-session", {
          amount: amountInSmallestUnit,
          currency: currency,
          description: projectTitle
            ? t("donate.stripe.donationWithTitle", { title: projectTitle })
            : t("donate.stripe.descriptionFallback"),
          projectId: projectId || null,
          projectSlug: projectSlug || "",
        });

        if (response.data && response.data.url) {
          // Przekierowanie na stronę płatności Stripe
          window.location.href = response.data.url;
        } else {
          throw new Error(t("donate.errors.missingStripeUrl"));
        }
      } catch (error) {
        console.error("Błąd płatności Stripe:", error);
        // Pobieramy wiadomość o błędzie z API lub używamy domyślnej
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          t("donate.errors.genericPaymentError");
        setErrorMsg(message);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Dla przelewu tradycyjnego zamykamy modal (można tu dodać zapisywanie do bazy "deklaracji wpłaty" w przyszłości)
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* WYŚWIETLANIE BŁĘDU (Portal renderuje się w document.body) */}
      {errorMsg && (
        <Alert
          variant="error"
          onClose={() => setErrorMsg(null)}
          autoClose={5000}
        >
          {errorMsg}
        </Alert>
      )}

      <div className="donate-modal" role="dialog" aria-modal="true">
        <button
          className="donate-modal__backdrop"
          onClick={onClose}
          aria-label={t("donate.aria.close")}
        />

        <div className="donate-modal__panel">
          <div className="donate-modal__header">
            <div className="donate-modal__title-wrap">
              <div className="donate-modal__kicker">{t("donate.kicker")}</div>
              <h3 className="donate-modal__title">
                {projectTitle || t("donate.titleFallback")}
              </h3>
            </div>

            <button className="donate-modal__close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>

          <div className="donate-modal__body">
            <div className="donate-modal__section">
              <div className="donate-modal__label">
                {t("donate.currency.label")}
              </div>

              <div className="donate-modal__segmented" role="tablist">
                <button
                  type="button"
                  className={`donate-modal__seg ${
                    currency === "PLN" ? "is-active" : ""
                  }`}
                  onClick={() => setCurrency("PLN")}
                >
                  <Coins size={16} />
                  PLN
                </button>

                <button
                  type="button"
                  className={`donate-modal__seg ${
                    currency === "EUR" ? "is-active" : ""
                  }`}
                  onClick={() => setCurrency("EUR")}
                >
                  <BadgeEuro size={16} />
                  EUR
                </button>
              </div>
            </div>

            <div className="donate-modal__section">
              <div className="donate-modal__label">
                {t("donate.amount.label")}
              </div>

              <div className="donate-modal__presets">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`donate-modal__preset ${
                      Number(amountNumber) === v ? "is-active" : ""
                    }`}
                    onClick={() => handlePreset(v)}
                  >
                    {formatMoney(v, currency)}
                  </button>
                ))}
              </div>

              <div className="donate-modal__amount-row">
                <label className="donate-modal__amount-input">
                  <span>{t("donate.amount.customLabel")}</span>
                  <input
                    inputMode="decimal"
                    value={amountStr}
                    onChange={handleAmountChange}
                    onBlur={handleAmountBlur}
                    placeholder={t("donate.amount.placeholder")}
                    maxLength={10}
                    disabled={isLoading} // Blokowanie podczas ładowania
                  />
                </label>

                <div className="donate-modal__amount-preview">
                  {amountStr === ""
                    ? "—"
                    : formatMoney(Number(amountNumber || 0), currency)}
                </div>
              </div>
            </div>

            <div className="donate-modal__section">
              <div className="donate-modal__label">
                {t("donate.method.label")}
              </div>

              <div className="donate-modal__methods">
                <button
                  type="button"
                  className={`donate-modal__method ${
                    method === "stripe" ? "is-active" : ""
                  }`}
                  onClick={() => setMethod("stripe")}
                  disabled={isLoading} // Blokowanie podczas ładowania
                >
                  <CreditCard size={18} />
                  <div>
                    <div className="donate-modal__method-title">
                      {t("donate.method.online.title")}
                    </div>
                    <div className="donate-modal__method-desc">
                      {currency === "PLN"
                        ? t("donate.method.online.descPLN")
                        : t("donate.method.online.descEUR")}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className={`donate-modal__method ${
                    method === "transfer" ? "is-active" : ""
                  }`}
                  onClick={() => setMethod("transfer")}
                  disabled={isLoading} // Blokowanie podczas ładowania
                >
                  <Landmark size={18} />
                  <div>
                    <div className="donate-modal__method-title">
                      {t("donate.method.transfer.title")}
                    </div>
                    <div className="donate-modal__method-desc">
                      {t("donate.method.transfer.desc")}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {method === "transfer" ? (
              <div className="donate-modal__section donate-modal__details">
                <div className="donate-modal__label">
                  {t("donate.transfer.detailsTitle")}
                </div>

                {!account ? (
                  <div className="donate-modal__note">
                    {t("donate.transfer.noAccount", { currency })}
                  </div>
                ) : (
                  <div className="donate-modal__bank">
                    <div className="donate-modal__bank-row">
                      <span>{t("donate.transfer.recipient")}</span>
                      <strong>{account.recipient}</strong>
                    </div>
                    <div className="donate-modal__bank-row">
                      <span>{t("donate.transfer.title")}</span>
                      <strong>{account.title || transferTitle}</strong>
                    </div>
                    <div className="donate-modal__bank-row">
                      <span>{t("donate.transfer.account")}</span>
                      <strong>{account.iban}</strong>
                    </div>
                    {account.swift && (
                      <div className="donate-modal__bank-row">
                        <span>SWIFT/BIC</span>
                        <strong>{account.swift}</strong>
                      </div>
                    )}
                    {account.bankName && (
                      <div className="donate-modal__bank-row">
                        <span>{t("donate.transfer.bank")}</span>
                        <strong>{account.bankName}</strong>
                      </div>
                    )}

                    <div className="donate-modal__hint">
                      {t("donate.transfer.amountToPay")}{" "}
                      <strong>
                        {amountStr === ""
                          ? "—"
                          : formatMoney(Number(amountNumber || 0), currency)}
                      </strong>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="donate-modal__section donate-modal__details">
                <div className="donate-modal__secure-info">
                  <ShieldCheck
                    className="donate-modal__secure-icon"
                    size={24}
                  />
                  <div className="donate-modal__secure-content">
                    <h4>{t("donate.secure.title")}</h4>
                    <p>{t("donate.secure.desc")}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="donate-modal__footer">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={isLoading}
            >
              {t("donate.actions.close")}
            </Button>

            <Button
              variant="primary"
              size="md"
              onClick={handleContinue}
              disabled={isLoading}
            >
              {/* Jeśli ładuje -> pokaż loader, w przeciwnym razie tekst dla wybranej metody */}
              {isLoading ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Loader size="sm" variant="inline" />{" "}
                  {t("donate.actions.processing")}
                </span>
              ) : method === "stripe" ? (
                t("donate.actions.goToPayment")
              ) : (
                t("donate.actions.done")
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default DonateModal;
